import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast'; // FIX: named import
// Removed undefined actions
import { useDispatch, useSelector } from 'react-redux';
// Removed undefined image
import { forgotPasswordAction, verifyForgotPasswordAction ,clearVerifyForgotPasswordAction,clearForgotPasswordAction, getAllTenantsAction } from '../../store/actions';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  // isOtpVerified removed for single-step verify
  const [timer, setTimer] = useState(120);
  const [otpExpired, setOtpExpired] = useState(false);
  // Company dropdown state
  const [selectedCompany, setSelectedCompany] = useState('');


  // Get all tenants for dropdown
  const { successData: TenantsData, loading: loadingCompanies, error: companyError } = useSelector(state => state.getAllTenantsReducer || {});
  const { successData, error, errorInfo } = useSelector(state => state.forgotPasswordReducer);
  const { successData: otpVerifyData, error: otpVerifyError, errorInfo: otpVerifyErrorInfo } = useSelector(state => state.verifyForgotPasswordReducer || {});
  // Fetch tenants on mount
  useEffect(() => {
    dispatch(getAllTenantsAction());
  }, [dispatch]);

  const validationSchema = React.useMemo(() =>
    Yup.object({
      company: Yup.string().required('Company is required'),
      phone_number: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Phone number must be 10 digits and start with 6,7,8,9")
        .required("Phone number is required"),
      otp: otpSent
        ? Yup.string().required('OTP is required').matches(/^\d{4}$/, 'OTP must be exactly 4 digits')
        : Yup.string().optional(),
      newPassword: otpSent
        ? Yup.string().min(8, "Password must be at least 8 characters").required("New Password is required")
        : Yup.string().optional(),
      confirmPassword: otpSent
        ? Yup.string()
          .min(8, "Password must be at least 8 characters")
          .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
          .required("Confirm Password is required")
        : Yup.string().optional(),
    })
    , [otpSent]);

  const formik = useFormik({
    initialValues: {
      company: '',
      phone_number: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const selectedTenant = (TenantsData?.data || []).find(t => t.identifier === values.company);
        if (!otpSent) {
          // Send OTP (identifier in body)
          dispatch(forgotPasswordAction({ identifier: values.company, mobile: values.phone_number }));
          setSelectedCompany(values.company);
          setOtpSent(true);
        } else {
          // Verify OTP and update password in one step
          dispatch(verifyForgotPasswordAction({ tenantId: selectedTenant?.id, mobile: values.phone_number, otp: values.otp, newPassword: values.newPassword }));
        }
      } catch (err) {
        console.error(err);
        toast.error('Something went wrong');
      }
    }
  });

  const handleResendOtp = () => {
    setOtpSent(false);
    setTimer(120);
    formik.setFieldValue('otp', '');
    formik.setFieldValue('newPassword', '');
    formik.setFieldValue('confirmPassword', '');
    dispatch(forgotPasswordAction({ identifier: formik.values.company, mobile: formik.values.phone_number }));
    setOtpSent(true);
  };

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0) {
      setOtpExpired(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  // Send OTP result
  useEffect(() => {
    if (successData) toast.success(successData.message);
    if (error && errorInfo?.message) toast.error(errorInfo.message);
    // ...existing code...
  }, [successData, error, errorInfo]);

  // Verify OTP & password result
  useEffect(() => {
    if (otpVerifyData) {
      toast.success(otpVerifyData.message || 'Password updated successfully');
      setOtpExpired(false);
      setTimer(0);
      // Redirect to login after short delay
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
    if (otpVerifyError) {
      toast.error(otpVerifyErrorInfo?.message || 'Invalid OTP or password');
    }
  }, [otpVerifyData, otpVerifyError, otpVerifyErrorInfo, dispatch, navigate]);

  useEffect(() => {
    if (open) {
      // Clear OTP/password state and toasts when modal opens
      dispatch(clearVerifyForgotPasswordAction());
      dispatch(clearForgotPasswordAction());
    }
    return () => {
      formik.resetForm();
      setOtpSent(false);
      setTimer(120);
      setOtpExpired(false);
      dispatch(clearVerifyForgotPasswordAction());
      dispatch(clearForgotPasswordAction());
    }
  }, [open, dispatch])

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fa" }}>
      <Paper sx={{ display: "flex", flexDirection: { xs: "column", sm: "row", md: "row" }, width: { xs: "90%", md: "900px", sm: "600px" }, maxWidth: "900px", borderRadius: "15px", overflow: "hidden", boxShadow: 3 }}>
        <Box sx={{ width: { xs: "100%", sm: "50%", md: "50%" }, backgroundColor: "#E2F0FF", display: { xs: "none", sm: "flex", md: "flex" }, flexDirection: "column", alignItems: "center", justifyContent: "center", pb: 5 }}>
          <Typography sx={{ textAlign: "center", mt: 2, color: '#000', fontWeight: 'bold', width: '60%' }}>
            Access your dashboard in just a few clicks.
          </Typography>
          <Typography sx={{ textAlign: "center", mt: 1, color: '#000', width: '80%', fontSize: '14px' }}>
            Connect your bank card, and create accounts in the selected currency.
          </Typography>
        </Box>
        <Box sx={{ width: { xs: "100%", md: "50%" }, padding: { xs: '20px', sm: "40px", md: "40px" }, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative" }}>
          <Box sx={{ width: "500px", p: 3 }}>
            <Typography sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: 24 }}>Forgot Password</Typography>
            <form onSubmit={formik.handleSubmit}>
              {/* Company Dropdown */}
              <TextField
                select
                label="Select Company"
                id="company"
                fullWidth
                SelectProps={{ native: true }}
                {...formik.getFieldProps('company')}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
                sx={{ width: '100%', maxWidth: '400px', mb: 2 }}
                disabled={otpSent}
              >
                <option value="">Select Company</option>
                {(TenantsData?.data || []).map((tenant) => (
                  <option key={tenant.id} value={tenant.identifier}>{tenant.companyName}</option>
                ))}
              </TextField>
              <TextField
                autoFocus
                margin="dense"
                id="phone_number"
                label="Phone number"
                type="tel"
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                autoComplete="off"
                {...formik.getFieldProps('phone_number')}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, '');
                  formik.setFieldValue('phone_number', onlyDigits);
                }}
                error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                helperText={formik.touched.phone_number && formik.errors.phone_number}
                sx={{ width: '100%', maxWidth: '400px' }}
                disabled={otpSent}
              />

              {otpSent && (
                <>
                  <TextField
                    margin="dense"
                    id="otp"
                    label="OTP"
                    type="text"
                    fullWidth
                    variant="outlined"
                    {...formik.getFieldProps('otp')}
                    error={formik.touched.otp && Boolean(formik.errors.otp)}
                    helperText={formik.touched.otp && formik.errors.otp}
                    sx={{ width: '100%', maxWidth: '400px' }}
                  />
                  <TextField
                    margin="dense"
                    id="newPassword"
                    label="New Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    {...formik.getFieldProps('newPassword')}
                    error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                    helperText={formik.touched.newPassword && formik.errors.newPassword}
                    sx={{ width: '100%', maxWidth: '400px' }}
                  />
                  <TextField
                    margin="dense"
                    id="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    {...formik.getFieldProps('confirmPassword')}
                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    sx={{ width: '100%', maxWidth: '400px' }}
                  />
                  {otpExpired ? (
                    <Alert severity="warning" sx={{ width: '100%', mb: 2, padding: '0px', fontSize: '0.875rem' }}>
                      Your OTP has expired. <Button onClick={handleResendOtp} sx={{ color: '#FC903D' }}>Resend OTP</Button>
                    </Alert>
                  ) : (
                    <Typography sx={{ color: 'gray', mb: 2 }}>
                      OTP expires in {timer} seconds
                    </Typography>
                  )}
                </>
              )}

              <Box sx={{ display: 'flex', width: '80%' }}>
                {!otpSent && (
                  <Button
                    type="submit"
                    sx={{ mt: 2, backgroundColor: '#3A349B', color: '#fff', width: '100%', maxWidth: '400px' }}
                    disabled={!(formik.values.company && formik.values.phone_number && !formik.errors.phone_number)}
                  >
                    Send OTP
                  </Button>
                )}
                {otpSent && (
                  <Button type="submit" sx={{ mt: 2, backgroundColor: '#3A349B', color: '#fff' }}>
                    Verify & Update Password
                  </Button>
                )}
                <Button
                  onClick={() => { navigate('/login') }}
                  sx={{ height: '40px', mt: 2, ml: 1, color: 'gray', textTransform: 'none', border: '1px solid gray' }}
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordModal;